import { Fragment, memo, useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'

import { ElasticFormModal, FormRow, HooksService, IconButton, TextField } from 'basic-components'
import { Add, Remove } from 'basic-components/icons'
import { DataStorageQuery } from 'dashboard-services/api/dataStorage'
import Query from 'dashboard-services/api/Query'
import { CommonService } from 'dashboard-services/services'
import { globalActions, userAuthQueries } from 'primary-components'

import { basicSettingsActions } from 'actions/settings'
import getApiConfig from 'api/ApiConfig'

import { useQueryClient } from '@tanstack/react-query'
import { When } from 'react-if'

import './DefaultColumnsListModal.scss'

const DefaultColumnsListModal = ({ close, onChangeColumns }) => {
  const dispatch = useDispatch(),
        defaultColumns = useSelector(state => state.settingsState.columns.length === 0 ? [""] : state.settingsState.columns),
        [columns, setColumns] = useState(defaultColumns),
        onChange = useCallback((val, index) => setColumns(columns => columns.replace(val, index)), []),
        deleteColumn = useCallback(index => setColumns(columns => columns.remove(index)), []),
        addColumn = useCallback(() => setColumns(columns => columns.add("")), []),
        prevLength = HooksService.usePrevious(columns?.length),
        getQueryBuilder = useCallback(() => new Query.Builder(dispatch(getApiConfig())), [dispatch]),
        { mutateAsync: updateSettingsCall } = userAuthQueries.useUpdateCurrentUserSettings({ getQueryBuilder }),
        lastRef = useRef(),
        queryClient = useQueryClient(),
        onSubmit = useCallback(() =>
          updateSettingsCall({ settings: { columns: JSON.stringify(columns) } })
            .then(() => close())
            .then(() => dispatch(globalActions.getMessageStore()).success("Default columns has been saved."))
            .then(() => dispatch(basicSettingsActions.onChange(columns, "columns")))
            .then(() => queryClient.invalidateQueries({ queryKey: DataStorageQuery.KEYS.GET_SYMBOL_COLUMNS() }))
            .then(() => onChangeColumns?.(undefined))
        , [updateSettingsCall, columns, close, dispatch, queryClient, onChangeColumns])

  useEffect(() => {
    if(prevLength < columns?.length) {
      lastRef.current && lastRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [columns?.length, prevLength])

  return (
    <ElasticFormModal
        additionalFooterContent={(
          <IconButton
              className="ng-office-app__authed__content__basket__body__data__basket__headers__header__settings__default-columns__add"
              onClick={addColumn}
              type="button"
          >
            <Add
                color="blue-bright"
                height={16}
                width={16}
            />
            <p>Add column</p>
          </IconButton>
        )}
        className="ng-office-app__authed__content__basket__body__data__basket__headers__header__settings__default-columns"
        onClose={close}
        onSubmit={onSubmit}
        open
        submitText="Save"
        title="Default column list"
    >
      <div className="ng-office-app__authed__content__basket__body__data__basket__headers__header__settings__default-columns__body">
        <div className="ng-office-app__authed__content__basket__body__data__basket__headers__header__settings__default-columns__subtitle">
          Select column that will be used by default
        </div>
        <div className="ng-office-app__authed__content__basket__body__data__basket__headers__header__settings__default-columns__rows">
          {columns.map((col, index) => (
            <Fragment key={`default-columns-modal-row-${index}`}>
              <When condition={index > 0}>
                {() => (
                  <div className="ng-office-app__authed__content__basket__body__data__basket__headers__header__settings__default-columns__fallback">
                    {`If ${CommonService.toOrdinalNumber(index)} doesn't exist, take:`}
                  </div>
                )}
              </When>
              <FormRow className="ng-office-app__authed__content__basket__body__data__basket__headers__header__settings__default-columns__row">
                <div
                    className="ng-office-app__authed__content__basket__body__data__basket__headers__header__settings__default-columns__row__index"
                    ref={index === columns.length - 1 ? lastRef : null}
                >
                  <p>{index + 1}</p>
                </div>
                <div className="ng-office-app__authed__content__basket__body__data__basket__headers__header__settings__default-columns__row__input">
                  <TextField
                      defaultValue={col}
                      name={"column" + index}
                      onChange={value => onChange(value, index)}
                      placeholder="Column name"
                      required
                      variant="border"
                  />
                </div>
                <When condition={columns.length > 1}>
                  {() => (
                    <div
                        className="ng-office-app__authed__content__basket__body__data__basket__headers__header__settings__default-columns__row__remove"
                        onClick={() => deleteColumn(index)}
                    >
                      <Remove color="red-dark"/>
                    </div>
                  )}
                </When>
              </FormRow>
            </Fragment>
          ))}
        </div>
      </div>
    </ElasticFormModal>
  )
}

DefaultColumnsListModal.propTypes = {
  close: PropTypes.func.isRequired,
  onChangeColumns: PropTypes.func
}

DefaultColumnsListModal.defaultProps = {
  onChangeColumns: undefined
}

export default memo(DefaultColumnsListModal)
